import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getPublicProfile, logProfileEvent, submitLead, submitReview, checkReview, getMe, getProfileNotifications, visitorPushSubscribe, visitorPushUnsubscribe, visitorPushCheck, notifyPwaAttempt, createProfilePayment, getAvailableSlots, bookAppointment, getVisitorAppointments, cancelVisitorAppointment, getAppointmentIcalUrl } from "../services/api";
import { useAppConfig } from "../context/AppConfigContext";
import translations from "../i18n/translations";
import { QRCodeSVG } from "qrcode.react";
import {
  Mail,
  Phone,
  MapPin,
  Globe,
  ExternalLink,
  Share2,
  Download,
  QrCode,
  X as XIcon,
  ArrowRight,
  Volume2,
  VolumeX,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Languages,
  Send,
  Check,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Star,
  Bell,
  BellRing,
  WifiOff,
  Camera,
  Copy,
  DollarSign,
  Loader2,
  Calendar,
  Clock,
  Pin,
} from "lucide-react";

/* ============================================================
   STANDALONE TRANSLATION HOOK FOR PUBLIC PAGE
   ============================================================ */

function detectPublicLanguage() {
  const stored = localStorage.getItem("app_language");
  if (stored && translations[stored]) return stored;
  const browserLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  if (browserLang.startsWith("es")) return "es";
  return "en";
}

const PublicLangContext = createContext();

function PublicLangProvider({ children }) {
  const [language, setLangState] = useState(detectPublicLanguage);

  const setLanguage = useCallback((lang) => {
    localStorage.setItem("app_language", lang);
    setLangState(lang);
  }, []);

  const t = useCallback(
    (key) => translations[language]?.[key] || translations.en[key] || key,
    [language]
  );

  return (
    <PublicLangContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </PublicLangContext.Provider>
  );
}

function usePublicTranslation() {
  return useContext(PublicLangContext);
}

/* ============================================================
   LANGUAGE SELECTOR (small, unobtrusive)
   ============================================================ */

function PublicLanguageSwitcher({ language, setLanguage, theme }) {
  const [open, setOpen] = useState(false);
  const isDark = theme.page.includes("950");

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 p-2 rounded-full text-xs font-medium shadow-sm backdrop-blur-md transition ${
          isDark
            ? "bg-white/10 hover:bg-white/20 text-white"
            : "bg-white/80 hover:bg-white text-gray-700"
        }`}
      >
        <Languages className="w-3.5 h-3.5" />
        {language.toUpperCase()}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={`absolute right-0 mt-1 rounded-lg shadow-lg border z-20 py-1 min-w-[80px] ${
            isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          }`}>
            {Object.keys(translations).map((lang) => (
              <button
                key={lang}
                onClick={() => { setLanguage(lang); setOpen(false); }}
                className={`w-full px-3 py-1.5 text-xs text-left font-medium transition ${
                  language === lang
                    ? isDark ? "text-white bg-white/10" : "text-blue-600 bg-blue-50"
                    : isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   THEME STYLE DEFINITIONS
   ============================================================ */

const THEME_STYLES = {
  modern: {
    page: "bg-gray-50",
    coverHeight: "h-[200px] lg:h-[280px]",
    coverGradient: (c) => `linear-gradient(135deg, ${c}, ${c}cc)`,
    card: "bg-white rounded-xl shadow-sm",
    profileImageClass: "rounded-full border-4 border-white shadow-lg",
    nameClass: "text-2xl lg:text-3xl font-bold text-gray-900",
    taglineClass: "text-gray-500 mt-1 lg:text-lg",
    badgeClass: "rounded-full text-white text-xs font-medium px-3 py-1",
    sectionTitle: "text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3",
    contactItem: "flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group",
    contactIcon: "w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-12 h-12 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity",
    ctaButton: "inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold shadow-md hover:shadow-lg hover:opacity-90 transition-all",
    galleryGrid: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-3",
    galleryItem: "rounded-lg",
    shareBar: "bg-white border-t border-gray-200",
  },
  classic: {
    page: "bg-gray-100",
    coverHeight: "h-[180px] lg:h-[260px]",
    coverGradient: (c) => `linear-gradient(180deg, ${c}, ${c}55)`,
    card: "bg-white rounded-sm border border-gray-200 shadow-none",
    profileImageClass: "rounded-sm border-4 border-white shadow-md",
    nameClass: "text-3xl lg:text-4xl font-serif font-bold text-gray-900",
    taglineClass: "text-gray-500 mt-1 italic font-serif lg:text-lg",
    badgeClass: "rounded-sm border border-current text-xs font-medium px-3 py-1 uppercase tracking-widest",
    sectionTitle: "text-xs font-bold text-gray-500 uppercase tracking-[0.2em] mb-3 border-b border-gray-200 pb-2",
    contactItem: "flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors group",
    contactIcon: "w-10 h-10 rounded-sm border-2 flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-11 h-11 rounded-sm border-2 border-current flex items-center justify-center hover:opacity-70 transition-opacity",
    ctaButton: "inline-flex items-center gap-2 px-8 py-3 rounded-sm border-2 text-white font-semibold uppercase tracking-wider hover:opacity-90 transition-all",
    galleryGrid: "grid grid-cols-2 lg:grid-cols-3 gap-3",
    galleryItem: "rounded-sm border border-gray-200",
    shareBar: "bg-gray-100 border-t border-gray-300",
  },
  minimal: {
    page: "bg-white",
    coverHeight: "h-[160px] lg:h-[220px]",
    coverGradient: (c) => `${c}11`,
    card: "bg-transparent rounded-none shadow-none border-b border-gray-100 pb-4",
    profileImageClass: "rounded-full border-2 border-gray-200",
    nameClass: "text-xl lg:text-2xl font-light text-gray-800 tracking-wide",
    taglineClass: "text-gray-400 mt-1 text-sm lg:text-base font-light",
    badgeClass: "rounded-full text-gray-500 text-xs font-light px-3 py-1 border border-gray-200",
    sectionTitle: "text-xs font-medium text-gray-500 uppercase tracking-[0.15em] mb-3",
    contactItem: "flex items-center gap-3 py-2 hover:text-gray-900 transition-colors group",
    contactIcon: "w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0",
    socialIcon: "w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-400 transition-all",
    ctaButton: "inline-flex items-center gap-2 px-6 py-2 rounded-full border border-gray-300 text-gray-700 font-light hover:border-gray-500 transition-all",
    galleryGrid: "grid grid-cols-3 lg:grid-cols-4 gap-1 lg:gap-2",
    galleryItem: "rounded-none",
    shareBar: "bg-white border-t border-gray-100",
  },
  bold: {
    page: "bg-gray-950",
    coverHeight: "h-[220px] lg:h-[300px]",
    coverGradient: (c) => `linear-gradient(160deg, ${c}, #000000)`,
    card: "bg-gray-900 rounded-2xl shadow-lg border border-gray-800",
    profileImageClass: "rounded-2xl border-4 border-gray-800 shadow-xl",
    nameClass: "text-3xl lg:text-4xl font-black text-white uppercase tracking-tight",
    taglineClass: "text-gray-400 mt-1 font-medium lg:text-lg",
    badgeClass: "rounded-lg text-white text-xs font-bold px-3 py-1 uppercase",
    sectionTitle: "text-sm font-bold text-gray-500 uppercase tracking-wider mb-3",
    contactItem: "flex items-center gap-3 p-3 rounded-xl hover:bg-gray-800 transition-colors group",
    contactIcon: "w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-12 h-12 rounded-xl flex items-center justify-center text-white hover:scale-110 transition-transform",
    ctaButton: "inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-white font-bold uppercase tracking-wider shadow-lg hover:scale-105 transition-transform",
    galleryGrid: "grid grid-cols-2 lg:grid-cols-3 gap-3",
    galleryItem: "rounded-xl",
    shareBar: "bg-gray-900 border-t border-gray-800",
  },
  pro: {
    page: "bg-gray-950",
    coverHeight: "h-[200px] lg:h-[280px]",
    coverGradient: (c) => `linear-gradient(135deg, ${c}88, ${c}22)`,
    card: "bg-white/5 backdrop-blur-md rounded-2xl border border-white/10",
    profileImageClass: "rounded-2xl border-2 border-white/20 shadow-2xl",
    nameClass: "text-2xl lg:text-3xl font-bold text-white",
    taglineClass: "text-gray-400 mt-1 lg:text-lg",
    badgeClass: "rounded-full text-white/70 text-xs font-medium px-3 py-1 bg-white/10",
    sectionTitle: "text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3",
    contactItem: "flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group",
    contactIcon: "w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-11 h-11 rounded-xl flex items-center justify-center text-white hover:scale-105 transition-transform",
    ctaButton: "inline-flex items-center gap-2 px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all",
    galleryGrid: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3",
    galleryItem: "rounded-xl",
    shareBar: "bg-gray-950/90 backdrop-blur-md border-t border-white/10",
  },
  instagram: {
    page: "bg-white",
    coverHeight: "h-0",
    coverGradient: () => "transparent",
    card: "bg-white border-b border-gray-200",
    profileImageClass: "rounded-full ring-[3px] ring-offset-2 ring-gradient-instagram",
    nameClass: "text-sm lg:text-base font-semibold text-gray-900",
    taglineClass: "text-gray-500 mt-0.5 text-sm lg:text-base",
    badgeClass: "rounded text-gray-500 text-xs font-medium px-2 py-0.5 bg-gray-100",
    sectionTitle: "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3",
    contactItem: "flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group",
    contactIcon: "w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-10 h-10 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity",
    ctaButton: "inline-flex items-center gap-2 px-6 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all",
    galleryGrid: "grid grid-cols-3 gap-[2px]",
    galleryItem: "rounded-none aspect-square",
    shareBar: "bg-white border-t border-gray-200",
  },
  instagram_dark: {
    page: "bg-gray-950",
    coverHeight: "h-0",
    coverGradient: () => "transparent",
    card: "bg-gray-950 border-b border-gray-800",
    profileImageClass: "rounded-full ring-[3px] ring-offset-2 ring-offset-gray-950 ring-gradient-instagram",
    nameClass: "text-sm lg:text-base font-semibold text-white",
    taglineClass: "text-gray-300 mt-0.5 text-sm lg:text-base",
    badgeClass: "rounded text-gray-300 text-xs font-medium px-2 py-0.5 bg-gray-800",
    sectionTitle: "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3",
    contactItem: "flex items-center gap-3 p-3 rounded-lg hover:bg-gray-900 transition-colors group",
    contactIcon: "w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0",
    socialIcon: "w-10 h-10 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity",
    ctaButton: "inline-flex items-center gap-2 px-6 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all",
    galleryGrid: "grid grid-cols-3 gap-[2px]",
    galleryItem: "rounded-none aspect-square",
    shareBar: "bg-gray-950 border-t border-gray-800",
  },
};

function getTheme(style) {
  return THEME_STYLES[style] || THEME_STYLES.modern;
}

/* ============================================================
   HELPERS
   ============================================================ */

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  x: XIcon,
  twitter: XIcon,
};

function getSocialIcon(platform) {
  return SOCIAL_ICONS[platform?.toLowerCase()] || Globe;
}

function getSocialLabel(platform) {
  const labels = {
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    x: "X",
    tiktok: "TikTok",
  };
  return labels[platform?.toLowerCase()] || platform || "Website";
}

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function ContactIcon({ type }) {
  switch (type) {
    case "email": return <Mail className="w-5 h-5" />;
    case "phone": return <Phone className="w-5 h-5" />;
    case "whatsapp": return <WhatsAppIcon className="w-5 h-5" />;
    case "address": return <MapPin className="w-5 h-5" />;
    default: return <Globe className="w-5 h-5" />;
  }
}

function getContactHref(item) {
  switch (item.contact_type || item.type) {
    case "email": return `mailto:${item.value}`;
    case "phone": return `tel:${item.value}`;
    case "whatsapp": return `https://wa.me/${item.value.replace(/[^0-9]/g, "")}`;
    case "address": return `https://maps.google.com/?q=${encodeURIComponent(item.value)}`;
    default: return item.value;
  }
}

/* ============================================================
   SIDEBAR (desktop only)
   ============================================================ */

function ProfileSidebar({ profile, profileName, themeColor, theme, profileUrl, onShare, onShowQR, onSaveContact, copied, t, isOwner, onProfilePicClick, hasWelcomeRing }) {
  const navigate = useNavigate();
  // Minimal theme needs a visible card style in the sidebar
  const isMinimal = theme.page === "bg-white";
  const sidebarCard = isMinimal
    ? "bg-white rounded-xl border border-gray-100 shadow-sm"
    : theme.card;
  const isDark = theme.page.includes("950");

  return (
    <div id="public-profile-sidebar" className="hidden lg:block lg:w-[320px] flex-shrink-0">
      <div className="sticky top-8 space-y-5">
        {/* Profile card */}
        <div className={`${sidebarCard} p-6 text-center relative group/edit`}>
          {isOwner && (
            <button
              onClick={() => navigate(`/app/profile/${profile.slug}?tab=general`)}
              className={`absolute top-2 right-2 p-1.5 rounded-full opacity-60 lg:opacity-0 lg:group-hover/edit:opacity-100 transition-opacity z-10 ${
                isDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-black/5 hover:bg-black/10 text-gray-600"
              }`}
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="relative w-[140px] h-[140px] mx-auto" style={hasWelcomeRing ? { padding: 3, background: `linear-gradient(135deg, ${themeColor}, ${themeColor}88)`, borderRadius: "9999px" } : undefined}>
            <div onClick={onProfilePicClick} className={`w-full h-full overflow-hidden bg-gray-200 ${theme.profileImageClass} ${!isOwner ? "cursor-pointer" : ""} ${hasWelcomeRing ? "ring-2 ring-white" : ""}`}>
              {profile.profile_image ? (
                <img src={profile.profile_image} alt={profileName} className="w-full h-full object-cover" />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white text-5xl font-bold"
                  style={{ backgroundColor: themeColor }}
                >
                  {profileName.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
            {isOwner && (
              <button
                onClick={() => { sessionStorage.setItem("pending_upload", "profile"); navigate(`/app/profile/${profile.slug}?tab=appearance`); }}
                className="absolute bottom-0 right-0 p-1.5 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition z-10"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <h1 className={`mt-4 ${theme.nameClass}`}>{profileName}</h1>
          {profile.tagline && <p className={theme.taglineClass}>{profile.tagline}</p>}
        </div>

        {/* Actions */}
        <div className={`${sidebarCard} p-4`}>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={onShare} className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-colors ${theme.page.includes("950") ? "hover:bg-gray-800 text-gray-400 hover:text-white" : "hover:bg-gray-50 text-gray-500 hover:text-gray-900"}`}>
              <Share2 className="w-5 h-5" />
              <span className="text-xs font-medium">{copied ? t("public.copied") : t("public.share")}</span>
            </button>
            <button onClick={onShowQR} className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-colors ${theme.page.includes("950") ? "hover:bg-gray-800 text-gray-400 hover:text-white" : "hover:bg-gray-50 text-gray-500 hover:text-gray-900"}`}>
              <QrCode className="w-5 h-5" />
              <span className="text-xs font-medium">{t("public.qrCode")}</span>
            </button>
            <button onClick={onSaveContact} className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-colors ${theme.page.includes("950") ? "hover:bg-gray-800 text-gray-400 hover:text-white" : "hover:bg-gray-50 text-gray-500 hover:text-gray-900"}`}>
              <Download className="w-5 h-5" />
              <span className="text-xs font-medium">{t("public.save")}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE
   ============================================================ */

export default function PublicProfilePage() {
  return (
    <PublicLangProvider>
      <PublicProfilePageInner />
    </PublicLangProvider>
  );
}

function PublicProfilePageInner() {
  const { slug } = useParams();
  const { socialBaseUrls, branding, appName } = useAppConfig();
  const { language, setLanguage, t } = usePublicTranslation();
  const [profile, setProfile] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [showPwaInstall, setShowPwaInstall] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showBellDropdown, setShowBellDropdown] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [showShareArrow, setShowShareArrow] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // Welcome video starts muted: phones block autoplay with sound
  const [welcomeMuted, setWelcomeMuted] = useState(true);
  const [unmuteHint, setUnmuteHint] = useState(false); // pulse the unmute button briefly
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [showPicMenu, setShowPicMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = location.pathname.startsWith("/app/preview/");

  // Read ref param from URL for tracking
  const refParam = new URLSearchParams(window.location.search).get("ref") || "";

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getPublicProfile(slug);

        // Handle slug redirect (guard against loops)
        if (res.data.redirect && res.data.redirect !== `/p/${slug}`) {
          window.location.replace(res.data.redirect);
          return;
        }

        setProfile(res.data.profile);
        setBlocks(res.data.blocks || []);
        setGalleries(res.data.galleries || []);
        logProfileEvent(slug, "view", refParam ? { ref: refParam } : {});

        // Check ownership
        const token = localStorage.getItem("token");
        if (token && res.data.profile?.user_id) {
          try {
            const meRes = await getMe();
            if (meRes.data.user?.id === res.data.profile.user_id) {
              setIsOwner(true);
            }
          } catch {} // eslint-disable-line
        }
      } catch (err) {
        // Handle redirect from error response
        if (err.response?.data?.redirect && err.response.data.redirect !== `/p/${slug}`) {
          window.location.replace(err.response.data.redirect);
          return;
        }
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  // Scroll to block anchor from URL hash — wait for welcome screen to close first
  const [pendingScroll, setPendingScroll] = useState(null);
  useEffect(() => {
    if (loading || !profile) return;
    const hash = window.location.hash;
    if (!hash?.startsWith("#block-")) return;
    const ws = profile.settings?.welcome_screen;
    const willShowWelcome = ws?.media_url && !localStorage.getItem(`welcome_skip_${slug}`) && (() => {
      const display = ws.display || "once";
      if (display === "never") return false;
      if (display === "scan") {
        const ref = new URLSearchParams(window.location.search).get("ref") || "";
        return ["qr", "nfc", "card"].includes(ref);
      }
      if (display === "once") return !localStorage.getItem(`welcome_seen_${slug}`);
      return true;
    })();
    if (willShowWelcome) {
      setPendingScroll(hash.slice(1));
    } else {
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }
  }, [loading, profile]);

  useEffect(() => {
    if (!showWelcome && pendingScroll) {
      setTimeout(() => {
        const el = document.getElementById(pendingScroll);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        setPendingScroll(null);
      }, 300);
    }
  }, [showWelcome, pendingScroll]);

  useEffect(() => {
    if (!showWelcome) return;
    setWelcomeMuted(true);
    setUnmuteHint(true);
    const timer = setTimeout(() => setUnmuteHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showWelcome]);

  // Auto-show welcome screen based on display setting
  useEffect(() => {
    if (!profile || isPreview) return;
    const ws = profile.settings?.welcome_screen;
    if (!ws?.media_url) return;
    if (localStorage.getItem(`welcome_skip_${slug}`)) return;
    const display = ws.display || "once";
    if (display === "never") return;
    if (display === "scan") {
      const ref = new URLSearchParams(window.location.search).get("ref") || "";
      if (!["qr", "nfc", "card"].includes(ref)) return;
    }
    if (display === "once") {
      const key = `welcome_seen_${slug}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    }
    setShowWelcome(true);
  }, [profile, slug, isPreview]);

  const handleProfilePicClick = () => {
    if (isOwner) return;
    const ws = profile.settings?.welcome_screen;
    if (ws?.media_url && profile.profile_image) {
      setShowPicMenu(true);
    } else if (ws?.media_url) {
      setShowWelcome(true);
    } else if (profile.profile_image) {
      setShowPhotoLightbox(true);
    }
  };

  // Inject Open Graph meta tags for share previews
  useEffect(() => {
    if (!profile) return;
    const origin = window.location.origin;
    const ogImage = profile.profile_image ? (profile.profile_image.startsWith("http") ? profile.profile_image : origin + profile.profile_image) : "";
    const tags = [
      { property: "og:title", content: profile.profile_name || "" },
      { property: "og:description", content: profile.tagline || profile.description || "" },
      { property: "og:image", content: ogImage },
      { property: "og:url", content: `${origin}/p/${slug}` },
      { property: "og:type", content: "profile" },
    ];
    const metas = tags.map(({ property, content }) => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", property);
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    });

    // Set favicon to profile image when SEO is enabled
    const addedLinks = [];
    if (profile.settings?.seo_enabled && (profile.profile_image || profile.cover_image)) {
      const iconSrc = profile.profile_image || profile.cover_image;
      const iconHref = iconSrc.startsWith("http") ? iconSrc : origin + iconSrc;

      // Remove existing favicons
      document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());

      const favicon = document.createElement("link");
      favicon.rel = "icon";
      favicon.type = "image/png";
      favicon.setAttribute("sizes", "192x192");
      favicon.href = iconHref;
      document.head.appendChild(favicon);
      addedLinks.push(favicon);

      const touchIcon = document.createElement("link");
      touchIcon.rel = "apple-touch-icon";
      touchIcon.setAttribute("sizes", "180x180");
      touchIcon.href = iconHref;
      document.head.appendChild(touchIcon);
      addedLinks.push(touchIcon);
    }

    return () => {
      metas.forEach((m) => { if (m.parentNode) m.parentNode.removeChild(m); });
      addedLinks.forEach((l) => { if (l.parentNode) l.parentNode.removeChild(l); });
    };
  }, [profile, slug]);

  // PWA: inject manifest link and register service worker
  useEffect(() => {
    if (!profile?.settings?.pwa_enabled) return;

    // Inject <link rel="manifest">
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = `/api/p/${slug}/manifest.json`;
    document.head.appendChild(link);

    // Inject <link rel="apple-touch-icon"> for iOS home screen icon
    const touchIcon = document.createElement("link");
    touchIcon.rel = "apple-touch-icon";
    touchIcon.setAttribute("sizes", "180x180");
    const iconPath = profile.settings?.pwa_icon || profile.profile_image || "";
    touchIcon.href = iconPath ? (iconPath.startsWith("http") ? iconPath : window.location.origin + iconPath) : "";
    if (touchIcon.href) document.head.appendChild(touchIcon);

    // Inject <meta name="theme-color">
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    const hadThemeMeta = !!themeMeta;
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = profile.theme_color || "#3B82F6";

    // Register service worker
    let swReg = null;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register(`/api/p/${slug}/sw.js`, { scope: `/p/${slug}` })
        .then((reg) => { swReg = reg; })
        .catch(() => {}); // silent fail
    }

    // Capture install prompt
    const handleInstallPrompt = (e) => {
      e.preventDefault();
      setPwaPrompt(e);
      // Show after a short delay so the page loads first
      setTimeout(() => setShowPwaInstall(true), 2000);
    };
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);

    // iOS detection
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isiOS && !isStandalone) {
      setTimeout(() => setShowPwaInstall(true), 2000);
    }

    return () => {
      document.head.removeChild(link);
      if (touchIcon.href && touchIcon.parentNode) {
        document.head.removeChild(touchIcon);
      }
      if (!hadThemeMeta && themeMeta.parentNode) {
        document.head.removeChild(themeMeta);
      }
      if (swReg) {
        swReg.unregister().catch(() => {});
      }
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, [profile, slug]);

  // Fetch notifications and check push subscription
  useEffect(() => {
    if (!profile?.settings?.pwa_enabled) return;
    // Record first visit timestamp (only set once)
    const firstVisitKey = `notif_first_visit_${slug}`;
    if (!localStorage.getItem(firstVisitKey)) {
      localStorage.setItem(firstVisitKey, new Date().toISOString());
    }
    const firstVisit = localStorage.getItem(firstVisitKey);

    getProfileNotifications(slug).then((res) => {
      const allNotifs = res.data.notifications || [];
      const dismissed = JSON.parse(localStorage.getItem(`notif_dismissed_${slug}`) || "[]");
      const notifs = allNotifs.filter((n) => {
        // Pinned notifications can't be dismissed (also restores ones dismissed before)
        if (n.pinned) return true;
        if (dismissed.includes(n.id)) return false;
        return n.created_at >= firstVisit;
      });
      setNotifications(notifs);
      const lastSeen = localStorage.getItem(`notif_seen_${slug}`) || "0";
      const unseen = notifs.filter((n) => n.created_at > lastSeen).length;
      setUnreadCount(unseen);
    }).catch(() => {});

    // Register SW for /p/ push notifications and check subscription
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.register("/sw.js").then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          if (sub) {
            visitorPushCheck(slug, sub.endpoint).then((res) => {
              setPushSubscribed(res.data.subscribed);
            }).catch(() => {});
          }
        });
      }).catch(() => {});
    }
  }, [profile, slug]);

  // Show notification prompt (after PWA install prompt is gone, not already subscribed, not dismissed too much)
  useEffect(() => {
    if (!profile?.settings?.pwa_enabled) return;
    if (pushSubscribed) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    // On iOS, only show if already in standalone mode (PWA installed)
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isiOS && !isStandalone) return;

    // Check dismiss count
    const dismissKey = `notif_prompt_dismissed_${slug}`;
    const dismissCount = parseInt(localStorage.getItem(dismissKey) || "0", 10);
    if (dismissCount >= 3) return;

    // Wait for PWA install prompt to go away, then show after delay
    const timer = setTimeout(() => {
      if (!showPwaInstall) {
        setShowNotifPrompt(true);
      }
    }, showPwaInstall ? 0 : 8000);

    return () => clearTimeout(timer);
  }, [profile, slug, pushSubscribed, showPwaInstall]);

  // When PWA install closes, show notification prompt after a short delay
  useEffect(() => {
    if (showPwaInstall || !profile?.settings?.pwa_enabled || pushSubscribed) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const dismissKey = `notif_prompt_dismissed_${slug}`;
    const dismissCount = parseInt(localStorage.getItem(dismissKey) || "0", 10);
    if (dismissCount >= 3) return;

    const timer = setTimeout(() => {
      setShowNotifPrompt(true);
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [showPwaInstall]);

  const dismissNotifPrompt = () => {
    setShowNotifPrompt(false);
    const dismissKey = `notif_prompt_dismissed_${slug}`;
    const count = parseInt(localStorage.getItem(dismissKey) || "0", 10);
    localStorage.setItem(dismissKey, String(count + 1));
  };

  const acceptNotifPrompt = async () => {
    setShowNotifPrompt(false);
    setPushLoading(true);
    try {
      const { getVapidPublicKey } = await import("../services/pushNotifications");
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) return;
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const padding = "=".repeat((4 - (vapidKey.length % 4)) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, "+").replace(/_/g, "/");
        const raw = atob(base64);
        const arr = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
      }
      await visitorPushSubscribe(slug, sub.toJSON());
      setPushSubscribed(true);
    } catch (err) {
      console.warn("Push subscribe failed:", err);
    } finally {
      setPushLoading(false);
    }
  };

  const handleTogglePush = async () => {
    if (pushLoading) return;
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        // Unsubscribe
        const reg = await navigator.serviceWorker.register("/sw.js");
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await visitorPushUnsubscribe(slug, sub.endpoint);
        }
        setPushSubscribed(false);
      } else {
        // Subscribe
        const { getVapidPublicKey } = await import("../services/pushNotifications");
        const vapidKey = await getVapidPublicKey();
        if (!vapidKey) return;
        const reg = await navigator.serviceWorker.register("/sw.js");
        await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          const padding = "=".repeat((4 - (vapidKey.length % 4)) % 4);
          const base64 = (vapidKey + padding).replace(/-/g, "+").replace(/_/g, "/");
          const raw = atob(base64);
          const arr = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
        }
        await visitorPushSubscribe(slug, sub.toJSON());
        setPushSubscribed(true);
      }
    } catch (err) {
      console.error("Push toggle error:", err);
    } finally {
      setPushLoading(false);
    }
  };

  const handleBellClick = () => {
    const next = !showBellDropdown;
    setShowBellDropdown(next);
    document.body.style.overflow = next ? "hidden" : "";
    document.documentElement.style.overflow = next ? "hidden" : "";
    if (next && notifications.length > 0) {
      localStorage.setItem(`notif_seen_${slug}`, notifications[0].created_at);
      setUnreadCount(0);
    }
  };

  const dismissNotification = (id) => {
    const dismissed = JSON.parse(localStorage.getItem(`notif_dismissed_${slug}`) || "[]");
    if (!dismissed.includes(id)) dismissed.push(id);
    localStorage.setItem(`notif_dismissed_${slug}`, JSON.stringify(dismissed));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const dismissAllNotifications = () => {
    const ids = notifications.filter((n) => !n.pinned).map((n) => n.id);
    const dismissed = JSON.parse(localStorage.getItem(`notif_dismissed_${slug}`) || "[]");
    const merged = [...new Set([...dismissed, ...ids])];
    localStorage.setItem(`notif_dismissed_${slug}`, JSON.stringify(merged));
    setNotifications((prev) => prev.filter((n) => n.pinned));
  };

  const profileUrl = window.location.origin + `/p/${slug}`;
  const shareUrl = `${profileUrl}?ref=share`;
  const themeColor = profile?.theme_color || "#3B82F6";
  const theme = getTheme(profile?.theme_style);
  const hasWelcomeRing = !!profile?.settings?.welcome_screen?.media_url;

  const handleShowQR = () => {
    logProfileEvent(slug, "qr_scan");
    setShowQR(true);
  };

  const handleShare = async () => {
    logProfileEvent(slug, "share", { ref: "share" });
    if (navigator.share) {
      try {
        await navigator.share({
          title: profile?.profile_name,
          text: profile?.tagline || profile?.profile_name,
          url: shareUrl,
        });
      } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveContact = () => {
    logProfileEvent(slug, "vcard_download");
    window.open(`/api/share/vcard/${slug}`, "_blank");
  };

  // PWA disabled: notify owner (must be before early returns for hooks rules)
  const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const pwaDisabled = isStandaloneMode && !profile?.settings?.pwa_enabled && !loading && !notFound && profile;
  useEffect(() => {
    if (pwaDisabled && slug) {
      notifyPwaAttempt(slug);
    }
  }, [pwaDisabled, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">{t("public.loading")}</p>
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 bg-gray-200 rounded-full flex items-center justify-center">
            <Globe className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("public.notFoundTitle")}</h1>
          <p className="text-gray-500">{t("public.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  // PWA disabled check: if running as installed PWA but pwa_enabled is off, block
  if (pwaDisabled) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 px-4">
        {/* Top bar: logo + language */}
        <div className="flex items-center justify-between py-4 max-w-sm mx-auto w-full">
          <img
            src={branding?.logo || "/branding/logo.png"}
            alt={appName}
            className="h-8 object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <PublicLanguageSwitcher language={language} setLanguage={setLanguage} theme={{ page: "" }} />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <WifiOff className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("public.pwaDisabledTitle")}</h1>
            <p className="text-gray-500 mb-4">{t("public.pwaDisabledDesc")}</p>

            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 text-left">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{t("public.pwaDisabledHowTo")}</p>
              {/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ? (
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>{t("public.pwaDisabledIos1")}</li>
                  <li>{t("public.pwaDisabledIos2")}</li>
                  <li>{t("public.pwaDisabledIos3")}</li>
                </ul>
              ) : (
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>{t("public.pwaDisabledAndroid1")}</li>
                  <li>{t("public.pwaDisabledAndroid2")}</li>
                </ul>
              )}
            </div>

            <p className="text-xs text-gray-400 mb-2">{t("public.pwaDisabledCopy")}</p>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <span className="text-sm text-gray-700 font-mono truncate flex-1">{window.location.origin}/p/{slug}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`);
                  const btn = document.getElementById("pwa-copy-btn");
                  if (btn) { btn.textContent = "✓"; setTimeout(() => { btn.textContent = t("public.pwaDisabledCopyBtn"); }, 2000); }
                }}
                id="pwa-copy-btn"
                className="shrink-0 px-3 py-1 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
              >
                {t("public.pwaDisabledCopyBtn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const profileName = profile.profile_name || profile.name || "";
  const isMinimal = profile?.theme_style === "minimal";
  const isInstagram = profile?.theme_style === "instagram" || profile?.theme_style === "instagram_dark";
  const isPro = profile?.theme_style === "pro";
  const noCover = isMinimal || isInstagram;

  return (
    <div>
      {/* Preview mode fixed bar */}
      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gray-900 text-white flex items-center px-4 py-2.5 shadow-lg" id="preview-bar">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-medium hover:text-blue-300 transition-colors"
          >
            <ChevronLeft size={18} />
            {t("preview.backToEditor")}
          </button>
          <span className="ml-auto text-xs text-gray-400">{t("preview.previewMode")}</span>
        </div>
      )}
      <div id="public-profile-page" className={`min-h-screen ${theme.page} pb-24 lg:pb-8 ${isPro ? "relative overflow-hidden" : ""} ${isPreview ? "pt-11" : ""}`}>

      {/* Language Selector + Bell */}
      <div className="fixed top-3 right-3 z-50 flex items-center gap-2">
        {profile?.settings?.pwa_enabled && (
          <div className="relative">
            <button
              onClick={handleBellClick}
              className={`relative p-2 rounded-full shadow-sm backdrop-blur-md transition ${
                theme.page.includes("950") || theme.page.includes("900")
                  ? "bg-white/10 hover:bg-white/20 text-white"
                  : "bg-white/80 hover:bg-white text-gray-700"
              }`}
            >
              {pushSubscribed ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {showBellDropdown && (
              <>
                <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => { setShowBellDropdown(false); document.body.style.overflow = ""; document.documentElement.style.overflow = ""; }} onTouchMove={(e) => e.preventDefault()} />
                <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-sm max-h-[80vh] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">{t("public.notifications")}</h3>
                    <div className="flex items-center gap-1.5">
                      {notifications.some((n) => !n.pinned) && (
                        <button
                          onClick={dismissAllNotifications}
                          className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                        >
                          {t("public.dismissAll")}
                        </button>
                      )}
                      {"serviceWorker" in navigator && "PushManager" in window && (
                        <button
                          onClick={handleTogglePush}
                          disabled={pushLoading}
                          className={`text-xs px-2 py-1 rounded-full font-medium transition ${
                            pushSubscribed
                              ? "bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700"
                              : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                          }`}
                        >
                          {pushLoading ? "..." : pushSubscribed ? t("public.disableNotifications") : t("public.enableNotifications")}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[60vh]">
                    {notifications.length === 0 ? (
                      <p className="px-4 py-8 text-center text-sm text-gray-400">{t("public.noNotifications")}</p>
                    ) : (
                      notifications.map((notif) => {
                        const Wrapper = notif.url ? "a" : "div";
                        const wrapperProps = notif.url ? { href: notif.url, target: notif.url.startsWith("http") ? "_blank" : "_self", rel: notif.url.startsWith("http") ? "noopener noreferrer" : undefined } : {};
                        return (
                          <div key={notif.id} className="relative group border-b border-gray-50">
                            <Wrapper {...wrapperProps} className={`block px-4 py-3 pr-9 hover:bg-gray-50 transition ${notif.url ? "cursor-pointer" : ""}`}>
                              {notif.image && (
                                <img src={notif.image} alt="" className="w-full rounded-lg object-cover mb-2 max-h-40" />
                              )}
                              <div className="flex items-center gap-1.5">
                                {notif.pinned && <Pin className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                                <p className="text-sm font-semibold text-gray-900">{notif.title}</p>
                              </div>
                              {notif.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-3">{notif.body}</p>}
                              <div className="flex items-center justify-between mt-1.5">
                                <p className="text-[10px] text-gray-400">
                                  {new Date(notif.created_at).toLocaleDateString(language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                </p>
                                {notif.url && <ExternalLink className="w-3 h-3 text-gray-400" />}
                              </div>
                            </Wrapper>
                            {!notif.pinned && (
                            <button
                              onClick={(e) => { e.stopPropagation(); dismissNotification(notif.id); }}
                              className="absolute top-2 right-2 p-1 rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 transition opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 max-sm:opacity-100"
                              title={t("public.dismiss")}
                            >
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <PublicLanguageSwitcher language={language} setLanguage={setLanguage} theme={theme} />
      </div>

      {/* Pro theme animated gradient mesh background */}
      {isPro && (
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full opacity-20 blur-[120px]" style={{ background: themeColor }} />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full opacity-15 blur-[100px]" style={{ background: themeColor }} />
          <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] rounded-full opacity-10 blur-[80px] bg-purple-500" />
        </div>
      )}

      {/* Instagram gradient ring style */}
      {isInstagram && (
        <style>{`
          .ig-ring {
            background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
            padding: 3px;
            border-radius: 9999px;
          }
          .ig-ring > div, .ig-ring > img {
            border: 3px solid ${profile?.theme_style === "instagram_dark" ? "#030712" : "white"};
            border-radius: 9999px;
          }
        `}</style>
      )}

      {/* Cover Image */}
      {!isInstagram && (
        <div id="public-profile-cover" className={`relative w-full ${theme.coverHeight}`}>
          {profile.cover_image ? (
            <img src={profile.cover_image} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: theme.coverGradient(themeColor) }} />
          )}
          {isOwner && (
            <button
              onClick={() => { sessionStorage.setItem("pending_upload", "cover"); navigate(`/app/profile/${profile.slug}?tab=appearance`); }}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition z-20"
            >
              <Camera className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Pro theme hero header */}
      {isPro && (
        <div id="public-profile-hero" className="relative z-10 max-w-4xl mx-auto px-4 lg:px-8 pt-8 pb-6">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6 lg:gap-10">
            <div className="relative w-[120px] h-[120px] lg:w-[160px] lg:h-[160px] flex-shrink-0" style={hasWelcomeRing ? { padding: 3, background: `linear-gradient(135deg, ${themeColor}, ${themeColor}88)`, borderRadius: "9999px" } : undefined}>
              <div onClick={handleProfilePicClick} className={`w-full h-full overflow-hidden bg-gray-800 ${theme.profileImageClass} ${!isOwner ? "cursor-pointer" : ""} ${hasWelcomeRing ? "ring-2 ring-gray-950" : ""}`}>
                {profile.profile_image ? (
                  <img src={profile.profile_image} alt={profileName} className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white text-4xl lg:text-5xl font-bold"
                    style={{ backgroundColor: themeColor }}
                  >
                    {profileName.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
              {isOwner && (
                <button
                  onClick={() => { sessionStorage.setItem("pending_upload", "profile"); navigate(`/app/profile/${profile.slug}?tab=appearance`); }}
                  className="absolute bottom-0 right-0 p-1.5 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition z-10"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0 text-center lg:text-left lg:pt-2">
              <h1 className={theme.nameClass}>{profileName}</h1>
              {profile.tagline && <p className={theme.taglineClass}>{profile.tagline}</p>}
              <div className="flex items-center justify-center lg:justify-start gap-3 mt-4">
                <button
                  onClick={handleShare}
                  className="px-5 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: themeColor, color: "white" }}
                >
                  {copied ? t("public.copied") : t("public.shareProfile")}
                </button>
                <button
                  onClick={handleShowQR}
                  className="px-5 py-2 rounded-xl bg-white/10 text-white text-sm font-semibold hover:bg-white/15 transition border border-white/10"
                >
                  {t("public.qrCode")}
                </button>
                <button
                  onClick={handleSaveContact}
                  className="px-5 py-2 rounded-xl bg-white/10 text-white text-sm font-semibold hover:bg-white/15 transition border border-white/10"
                >
                  {t("public.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instagram-style header */}
      {isInstagram && (() => {
        const igDark = profile?.theme_style === "instagram_dark";
        const igBorder = igDark ? "border-gray-800" : "border-gray-200";
        const igText = igDark ? "text-white" : "text-gray-900";
        const igSub = igDark ? "text-gray-300" : "text-gray-500";
        const igBtn = igDark ? "bg-gray-800 text-white hover:bg-gray-700" : "bg-gray-100 text-gray-900 hover:bg-gray-200";
        const igAvBg = igDark ? "bg-gray-800" : "bg-gray-200";
        return (
        <div id="public-profile-ig-header" className={`max-w-[935px] mx-auto px-4 lg:px-8 pt-6 lg:pt-10 border-b ${igBorder} pb-6 lg:pb-10 relative group/edit`}>
          {isOwner && (
            <button
              onClick={() => navigate(`/app/profile/${profile.slug}?tab=general`)}
              className={`absolute top-3 right-3 lg:right-8 p-1.5 rounded-full opacity-60 lg:opacity-0 lg:group-hover/edit:opacity-100 transition-opacity z-10 ${
                igDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-black/5 hover:bg-black/10 text-gray-600"
              }`}
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="flex items-start gap-6 lg:gap-16">
            <div className={`${hasWelcomeRing ? "" : "ig-ring"} flex-shrink-0 lg:mx-12`}>
              <div className="relative w-[80px] h-[80px] lg:w-[150px] lg:h-[150px]" style={hasWelcomeRing ? { padding: 3, background: `linear-gradient(135deg, ${themeColor}, ${themeColor}88)`, borderRadius: "9999px" } : undefined}>
                <div onClick={handleProfilePicClick} className={`w-full h-full overflow-hidden ${igAvBg} rounded-full ${!isOwner ? "cursor-pointer" : ""} ${hasWelcomeRing ? "ring-2 ring-white" : ""}`}>
                  {profile.profile_image ? (
                    <img src={profile.profile_image} alt={profileName} className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center text-white text-2xl lg:text-5xl font-bold rounded-full"
                      style={{ backgroundColor: themeColor }}
                    >
                      {profileName.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                </div>
                {isOwner && (
                  <button
                    onClick={() => { sessionStorage.setItem("pending_upload", "profile"); navigate(`/app/profile/${profile.slug}?tab=appearance`); }}
                    className="absolute bottom-0 right-0 p-1.5 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition z-10"
                  >
                    <Camera className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0 pt-1 lg:pt-2">
              <div className="lg:flex lg:items-center lg:gap-4 mb-2">
                <h1 className={`text-sm lg:text-xl font-semibold ${igText}`}>{profileName}</h1>
                <div className="hidden lg:flex items-center gap-2 mt-0">
                  <button
                    onClick={handleShare}
                    className={`px-5 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                  >
                    {copied ? t("public.copied") : t("public.share")}
                  </button>
                  <button
                    onClick={handleShowQR}
                    className={`px-5 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                  >
                    {t("public.qrCode")}
                  </button>
                  <button
                    onClick={handleSaveContact}
                    className={`px-5 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                  >
                    {t("public.save")}
                  </button>
                </div>
              </div>
              <div className={`hidden lg:flex items-center gap-8 text-base ${igText} mb-4`}>
                <span><strong>{blocks.length}</strong> {t("public.sections")}</span>
                <span><strong>{galleries.reduce((n, g) => n + (g.items?.length || 0), 0)}</strong> {t("public.photos")}</span>
              </div>
              {profile.tagline && <p className={`${igSub} text-sm lg:text-base`}>{profile.tagline}</p>}
              <div className="flex lg:hidden items-center gap-2 mt-3">
                <button
                  onClick={handleShare}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                >
                  {copied ? t("public.copied") : t("public.share")}
                </button>
                <button
                  onClick={handleShowQR}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                >
                  {t("public.qrCode")}
                </button>
                <button
                  onClick={handleSaveContact}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${igBtn}`}
                >
                  {t("public.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Desktop layout: sidebar + content */}
      <div className={`${isInstagram ? "max-w-[935px]" : isPro ? "max-w-4xl" : "max-w-5xl"} mx-auto px-4 lg:px-8`}>
        <div className={`${isInstagram || isPro ? "" : "lg:flex lg:gap-8"} relative z-10 ${isPro ? "mt-0" : noCover ? (isInstagram ? "mt-0" : "lg:mt-8") : "lg:-mt-20"}`}>

          {/* Sidebar — desktop only (not for Instagram or Pro) */}
          {!isInstagram && !isPro && (
            <ProfileSidebar
              profile={profile}
              profileName={profileName}
              themeColor={themeColor}
              theme={theme}
              profileUrl={profileUrl}
              onShare={handleShare}
              onShowQR={handleShowQR}
              onSaveContact={handleSaveContact}
              copied={copied}
              t={t}
              isOwner={isOwner}
              onProfilePicClick={handleProfilePicClick}
              hasWelcomeRing={hasWelcomeRing}
            />
          )}

          {/* Main content area */}
          <div className="flex-1 min-w-0">
            {/* Mobile-only profile header (not for Instagram or Pro — they have their own) */}
            {!isInstagram && !isPro && (
              <div className={`lg:hidden relative z-10 ${isMinimal ? "mt-4" : "-mt-16"}`}>
                <div className="flex justify-center">
                  <div className="relative w-[120px] h-[120px]" style={hasWelcomeRing ? { padding: 3, background: `linear-gradient(135deg, ${themeColor}, ${themeColor}88)`, borderRadius: "9999px" } : undefined}>
                    <div onClick={handleProfilePicClick} className={`w-full h-full overflow-hidden bg-gray-200 ${theme.profileImageClass} ${!isOwner ? "cursor-pointer" : ""} ${hasWelcomeRing ? "ring-2 ring-white" : ""}`}>
                      {profile.profile_image ? (
                        <img src={profile.profile_image} alt={profileName} className="w-full h-full object-cover" />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center text-white text-4xl font-bold"
                          style={{ backgroundColor: themeColor }}
                        >
                          {profileName.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => { sessionStorage.setItem("pending_upload", "profile"); navigate(`/app/profile/${profile.slug}?tab=appearance`); }}
                        className="absolute bottom-0 right-0 p-1.5 rounded-full bg-black/50 text-white/90 hover:bg-black/70 transition z-10"
                      >
                        <Camera className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-center mt-4">
                  <h1 className={theme.nameClass}>{profileName}</h1>
                  {profile.tagline && <p className={theme.taglineClass}>{profile.tagline}</p>}
                </div>
              </div>
            )}

            {/* Blocks */}
            <div id="public-profile-blocks" className={`${isInstagram || isPro ? "mt-6" : "mt-8 lg:mt-0"} space-y-6`}>
              {blocks.map((block) => (
                <div key={block.id} id={`block-${block.id}`} className={isOwner ? "relative group/edit" : ""}>
                  {isOwner && (
                    <button
                      onClick={() => navigate(`/app/profile/${profile.slug}?tab=${block.block_type === "about" ? "general" : "blocks"}&block=${block.id}`)}
                      className={`absolute top-2 right-2 p-1.5 rounded-full opacity-60 lg:opacity-0 lg:group-hover/edit:opacity-100 transition-opacity z-10 ${
                        theme.page.includes("950") ? "bg-white/10 hover:bg-white/20 text-white" : "bg-black/5 hover:bg-black/10 text-gray-600"
                      }`}
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <BlockRenderer block={block} profile={profile} themeColor={themeColor} theme={theme} slug={profile.slug} socialBaseUrls={socialBaseUrls} galleries={galleries} />
                </div>
              ))}
            </div>

            {/* Galleries rendered via gallery blocks */}
          </div>
        </div>
      </div>

      {/* Fixed Share Bar — mobile only */}
      {/* PWA Install Prompt */}
      {/* Notification prompt (text-only bottom sheet) */}
      {showNotifPrompt && !showPwaInstall && !showBellDropdown && (
        <div className="fixed z-50 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white rounded-xl shadow-lg border border-gray-200 p-4 animate-slide-up bottom-20 lg:bottom-4">
          <button
            onClick={dismissNotifPrompt}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
          >
            <XIcon size={16} />
          </button>
          <div className="pr-6">
            <p className="text-sm font-semibold text-gray-900">
              {t("public.notifPromptTitle").replace("{name}", profile?.profile_name || "")}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {t("public.notifPromptDesc")}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={acceptNotifPrompt}
                disabled={pushLoading}
                className="px-3 py-1.5 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                style={{ backgroundColor: themeColor }}
              >
                {pushLoading ? "..." : t("public.notifPromptAllow")}
              </button>
              <button
                onClick={dismissNotifPrompt}
                className="text-xs text-gray-400 hover:text-gray-600 transition"
              >
                {t("public.notifPromptDismiss")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPwaInstall && profile?.settings?.pwa_enabled && (
        <div className={`fixed z-50 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white rounded-xl shadow-lg border border-gray-200 p-4 animate-slide-up ${pwaPrompt ? "bottom-20 lg:bottom-4" : "bottom-20 lg:bottom-4"}`}>
          <button
            onClick={() => setShowPwaInstall(false)}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
          >
            <XIcon size={16} />
          </button>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              {(profile.settings?.pwa_icon || profile.profile_image) ? (
                <img
                  src={profile.settings?.pwa_icon || profile.profile_image}
                  alt=""
                  className="w-12 h-12 object-cover shadow-sm"
                  style={{ borderRadius: "22%" }}
                />
              ) : (
                <div className="w-12 h-12 flex items-center justify-center shadow-sm" style={{ borderRadius: "22%", backgroundColor: themeColor + "20" }}>
                  <Download size={22} style={{ color: themeColor }} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {t("public.installApp").replace("{name}", profile.settings?.pwa_name || profile.profile_name)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {pwaPrompt ? t("public.installAppDesc") : t("public.iosInstallDesc")}
              </p>
              {pwaPrompt ? (
                <button
                  onClick={async () => {
                    pwaPrompt.prompt();
                    const result = await pwaPrompt.userChoice;
                    if (result.outcome === "accepted") setShowPwaInstall(false);
                    setPwaPrompt(null);
                  }}
                  className="mt-2 px-3 py-1.5 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: themeColor }}
                >
                  {t("public.installButton")}
                </button>
              ) : (
                <div className="mt-3 space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPwaInstall(false);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      setTimeout(() => setShowShareArrow(true), 400);
                    }}
                    className="flex items-center gap-2.5 w-full text-left rounded-lg p-1.5 -m-1.5 hover:bg-blue-50/50 active:bg-blue-50 transition"
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-600">{t("public.iosStep1")}</p>
                  </button>
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="12" y1="8" x2="12" y2="16" />
                        <line x1="8" y1="12" x2="16" y2="12" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-600">{t("public.iosStep2")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iOS Share arrow overlay */}
      {showShareArrow && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => { setShowShareArrow(false); setShowPwaInstall(true); }}
          />
          <div className="fixed z-50 bottom-1 left-0 right-0 flex flex-col items-center animate-bounce pointer-events-none">
            <div className="bg-white rounded-xl shadow-lg px-5 py-3 text-center pointer-events-auto relative">
              <button
                onClick={() => { setShowShareArrow(false); setShowPwaInstall(true); }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 text-white rounded-full flex items-center justify-center shadow-md"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
              </button>
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-1 justify-center flex-wrap">
                {t("public.iosArrowText_before")}
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-500 flex-shrink-0 mx-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                {t("public.iosArrowText_after")}
              </p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 justify-center">
                {t("public.iosArrowStep2_before")}
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-500 flex-shrink-0 mx-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                {t("public.iosArrowStep2_after")}
              </p>
            </div>
            <svg className="w-8 h-8 text-white mt-1" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 16l-6-6h12l-6 6z" />
            </svg>
          </div>
        </>
      )}

      <div id="public-profile-share-bar" className={`fixed bottom-0 left-0 right-0 shadow-lg z-40 lg:hidden ${theme.shareBar}`}>
        <div className="max-w-lg mx-auto flex items-center justify-around py-3 px-4">
          <button onClick={handleShare} className="flex flex-col items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors">
            <Share2 className="w-5 h-5" />
            <span className="text-xs font-medium">{copied ? t("public.copied") : t("public.share")}</span>
          </button>
          <button onClick={handleShowQR} className="flex flex-col items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors">
            <QrCode className="w-5 h-5" />
            <span className="text-xs font-medium">{t("public.qrCode")}</span>
          </button>
          <button onClick={handleSaveContact} className="flex flex-col items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors">
            <Download className="w-5 h-5" />
            <span className="text-xs font-medium">{t("public.saveContact")}</span>
          </button>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div id="public-profile-qr-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowQR(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <XIcon className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{t("public.scanQrCode")}</h3>
            <p className="text-sm text-gray-500 mb-6">{t("public.shareInstantly")}</p>
            <div className="flex justify-center mb-4 relative">
              <QRCodeSVG
                value={`${profileUrl}?ref=qr`}
                size={200}
                level="H"
                fgColor={profile?.settings?.qr_use_color ? themeColor : "#000000"}
                includeMargin
              />
              {profile?.settings?.qr_show_logo && profile?.profile_image && (
                <img
                  src={profile.profile_image}
                  alt=""
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border-[4px] border-white shadow-sm object-cover"
                />
              )}
            </div>
            <p className="text-xs text-gray-400 break-all">{profileUrl}</p>
          </div>
        </div>
      )}

      {/* Profile Pic Tap Menu */}
      {showPicMenu && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowPicMenu(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xs overflow-hidden shadow-xl mb-0 sm:mb-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setShowPicMenu(false); setShowPhotoLightbox(true); }}
              className="w-full px-5 py-4 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 transition flex items-center gap-3 border-b border-gray-100"
            >
              <Camera className="w-5 h-5 text-gray-500" />
              {t("public.viewPhoto")}
            </button>
            <button
              onClick={() => { setShowPicMenu(false); setShowWelcome(true); }}
              className="w-full px-5 py-4 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 transition flex items-center gap-3 border-b border-gray-100"
            >
              <Star className="w-5 h-5 text-gray-500" />
              {t("public.viewWelcome")}
            </button>
            <button
              onClick={() => setShowPicMenu(false)}
              className="w-full px-5 py-3 text-center text-sm text-gray-400 hover:bg-gray-50 transition"
            >
              {t("public.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Welcome Screen Overlay */}
      {showWelcome && profile?.settings?.welcome_screen?.media_url && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowWelcome(false)}>
          <button onClick={() => setShowWelcome(false)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white text-gray-900 shadow-lg hover:bg-gray-100 transition">
            <XIcon className="w-6 h-6" />
          </button>
          <div className="relative max-w-lg w-full max-h-[70vh] px-4" onClick={(e) => e.stopPropagation()}>
            {profile.settings.welcome_screen.media_type === "video" ? (
              <>
                <video
                  // React doesn't reliably set the muted attribute, which autoplay needs
                  ref={(el) => { if (el) el.muted = welcomeMuted; }}
                  src={profile.settings.welcome_screen.media_url}
                  className="w-full max-h-[70vh] rounded-2xl object-contain"
                  autoPlay
                  muted={welcomeMuted}
                  controls
                  playsInline
                  onVolumeChange={(e) => setWelcomeMuted(e.currentTarget.muted)}
                  onEnded={() => setShowWelcome(false)}
                />
                {welcomeMuted && (
                  <button
                    onClick={() => setWelcomeMuted(false)}
                    className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-900 text-sm font-semibold shadow-lg hover:bg-gray-100 transition"
                  >
                    {/* Pulsing ring for the first seconds so visitors notice the video has sound */}
                    {unmuteHint && (
                      <span className="absolute inset-0 rounded-full bg-white/70 motion-safe:animate-ping pointer-events-none" />
                    )}
                    <VolumeX className="relative w-5 h-5" />
                    <span className="relative">{t("public.unmute")}</span>
                  </button>
                )}
                {!welcomeMuted && (
                  <button
                    onClick={() => setWelcomeMuted(true)}
                    className="absolute top-3 left-7 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                )}
              </>
            ) : (
              <img
                src={profile.settings.welcome_screen.media_url}
                alt="Welcome"
                className="w-full max-h-[70vh] rounded-2xl object-contain"
              />
            )}
          </div>
          <div className="max-w-lg w-full px-4 mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowWelcome(false);
              }}
              className="w-full flex items-center justify-center gap-3 py-3.5 px-5 rounded-2xl text-white text-base font-semibold shadow-lg hover:brightness-110 active:scale-[0.98] transition"
              style={{ backgroundColor: themeColor }}
            >
              {profile.profile_image && (
                <img src={profile.profile_image} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-white/70" />
              )}
              <span>{t("public.viewProfile")}</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
          {/* Only when it would auto-show again: "once" never repeats, "never" only opens on tap */}
          {["always", "scan"].includes(profile.settings.welcome_screen.display || "once") && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem(`welcome_skip_${slug}`, "1");
                setShowWelcome(false);
              }}
              className="mt-3 text-sm text-white/60 hover:text-white transition"
            >
              {t("public.dontShowAgain")}
            </button>
          )}
        </div>
      )}

      {/* Photo Lightbox */}
      {showPhotoLightbox && profile?.profile_image && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowPhotoLightbox(false)}>
          <button onClick={() => setShowPhotoLightbox(false)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition">
            <XIcon className="w-6 h-6" />
          </button>
          <div className="max-w-lg w-full max-h-[85vh] mx-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={profile.profile_image}
              alt={profile.profile_name}
              className="w-full max-h-[85vh] rounded-2xl object-contain"
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

/* ============================================================
   BLOCK RENDERER
   ============================================================ */

function BlockRenderer({ block, profile, themeColor, theme, slug, socialBaseUrls = {}, galleries = [] }) {
  const { language, t } = usePublicTranslation();
  const config = block.config || {};
  const lt = (fallbackKey) => config.title_i18n?.[language] || (fallbackKey ? t(fallbackKey) : "");

  switch (block.block_type) {
    case "location": {
      const address = config.address || "";
      if (!address.trim()) return null;
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      return (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${theme.card} p-5 lg:p-6 flex items-center gap-3 group transition hover:shadow-sm block`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: themeColor + "15" }}
          >
            <MapPin className="w-5 h-5" style={{ color: themeColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-sm font-semibold ${theme.page.includes("950") ? "text-white" : "text-gray-900"}`}>
              {lt("public.blockTitle.location")}
            </h3>
            <p className={`text-xs truncate ${theme.page.includes("950") ? "text-gray-400" : "text-gray-500"}`}>
              {address}
            </p>
          </div>
          <ExternalLink className={`w-4 h-4 flex-shrink-0 ${theme.page.includes("950") ? "text-gray-500" : "text-gray-400"} group-hover:translate-x-0.5 transition-transform`} />
        </a>
      );
    }

    case "map": {
      const { lat, lng, zoom = 15, address } = config;
      if (!lat || !lng) return null;
      const delta = 0.02 / (zoom / 10);
      const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lng) - delta},${parseFloat(lat) - delta},${parseFloat(lng) + delta},${parseFloat(lat) + delta}&layer=mapnik&marker=${lat},${lng}`;
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      return (
        <div className={`${theme.card} overflow-hidden`}>
          {(lt() || address) && (
            <div className="px-5 pt-5 lg:px-6 lg:pt-6 pb-2">
              {lt() && (
                <h3 className={`text-sm font-semibold ${theme.page.includes("950") ? "text-white" : "text-gray-900"}`}>
                  {lt()}
                </h3>
              )}
              {address && (
                <p className={`text-xs mt-0.5 ${theme.page.includes("950") ? "text-gray-400" : "text-gray-500"}`}>
                  {address}
                </p>
              )}
            </div>
          )}
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block relative group">
            <iframe
              title="map"
              width="100%"
              height="250"
              frameBorder="0"
              src={embedUrl}
              className="pointer-events-none"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5" />
                {t("public.openInMaps")}
              </span>
            </div>
          </a>
        </div>
      );
    }

    case "about": {
      const text = config.text_i18n?.[language] || config.text || profile.description || "";
      if (!text.trim()) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          <h3 className={theme.sectionTitle}>{lt("public.blockTitle.about")}</h3>
          <p className={`leading-relaxed whitespace-pre-line ${theme.page.includes("950") ? "text-gray-300" : "text-gray-700"}`}>
            {text}
          </p>
        </div>
      );
    }

    case "contact_info":
      if (!block.contact_items?.length) return null;
      if (!block.contact_items.some((i) => i.value && i.value.trim())) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          <h3 className={theme.sectionTitle}>{lt("public.blockTitle.contact")}</h3>
          <div className="space-y-1 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
            {(block.contact_items || []).filter((item) => item.value && item.value.trim()).map((item) => (
              <a
                key={item.id}
                href={getContactHref(item)}
                target={item.contact_type === "email" ? undefined : "_blank"}
                rel="noopener noreferrer"
                className={theme.contactItem}
                onClick={() => logProfileEvent(slug, "contact_click", { type: item.contact_type || item.type, label: item.label })}
              >
                <div className={theme.contactIcon} style={
                  theme.contactIcon.includes("text-gray")
                    ? {}
                    : theme.contactIcon.includes("border-2")
                      ? { borderColor: themeColor, color: themeColor, backgroundColor: "transparent" }
                      : { backgroundColor: themeColor, color: "white" }
                }>
                  <ContactIcon type={item.contact_type || item.type} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-xs capitalize ${theme.page.includes("950") ? "text-gray-500" : "text-gray-400"}`}>
                    {item.label || item.contact_type || item.type}
                  </p>
                  <p className={`text-sm truncate ${theme.page.includes("950") ? "text-gray-200" : "text-gray-800"}`}>{item.value}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-500 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      );

    case "social_links":
      if (!block.social_links?.length) return null;
      if (!block.social_links.some((l) => l.url && l.url.trim())) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          <h3 className={theme.sectionTitle}>{lt("public.blockTitle.social")}</h3>
          <div className="flex flex-wrap gap-3">
            {(block.social_links || []).filter((link) => link.url && link.url.trim()).map((link) => {
              const Icon = getSocialIcon(link.platform);
              const isBold = theme.page.includes("950");
              const isMinimal = theme.socialIcon.includes("text-gray");
              const baseUrl = socialBaseUrls[link.platform] || "";
              const fullUrl = link.platform === "website" || link.url.startsWith("http")
                ? link.url
                : baseUrl + link.url;
              return (
                <a
                  key={link.id}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={getSocialLabel(link.platform)}
                  className={theme.socialIcon}
                  style={isMinimal ? {} : { backgroundColor: themeColor, color: "white" }}
                  onClick={() => logProfileEvent(slug, "social_click", { platform: link.platform })}
                >
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        </div>
      );

    case "custom_text":
    case "header": {
      const content = config.content || "";
      const stripped = content.replace(/<[^>]*>/g, "").trim();
      const showTitle = config.show_title !== false;
      if (!stripped && !(showTitle && lt())) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          {showTitle && lt() && <h3 className={theme.sectionTitle}>{lt()}</h3>}
          {stripped && (
            <div
              className={`leading-relaxed rich-text ${theme.page.includes("950") ? "text-gray-300" : "text-gray-700"}`}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>
      );
    }

    case "cta_button":
      if (!(config.url || "").trim()) return null;
      return (
        <div>
          {lt() && <h3 className={theme.sectionTitle}>{lt()}</h3>}
          <div className="flex justify-center">
            <a
              href={config.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logProfileEvent(slug, "link_click", { label: lt(), url: config.url })}
              className={theme.ctaButton}
              style={{ backgroundColor: theme.ctaButton.includes("border") ? "transparent" : (config.color || themeColor), borderColor: config.color || themeColor, color: theme.ctaButton.includes("text-gray") ? undefined : "white" }}
            >
              {config.button_text || lt("public.blockTitle.learnMore")}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      );

    case "bank_info": {
      const bankFields = [
        { key: "bank_name", label: t("public.bank.bankName") },
        { key: "clabe", label: t("public.bank.clabe") },
        { key: "account_number", label: t("public.bank.accountNumber") },
        { key: "card_number", label: t("public.bank.cardNumber") },
        { key: "beneficiary", label: t("public.bank.beneficiary") },
        { key: "phone", label: t("public.bank.phone") },
      ].filter((f) => config[f.key]?.trim());
      if (!bankFields.length) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          <h3 className={theme.sectionTitle}>{lt("public.bank.title")}</h3>
          <button
            onClick={() => {
              const url = `${window.location.origin}/p/${slug}#block-${block.id}`;
              const text = `${profile.profile_name} - ${lt("public.bank.title")}\n${url}`;
              if (navigator.share) {
                navigator.share({ text }).catch(() => {});
              } else {
                navigator.clipboard.writeText(text);
              }
            }}
            className={`mb-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium transition ${
              theme.page.includes("950")
                ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <Share2 className="w-3.5 h-3.5" />
            {t("public.share")}
          </button>
          <div className="space-y-2">
            {bankFields.map(({ key, label }) => (
              <div
                key={key}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg ${
                  theme.page.includes("950") ? "bg-gray-800/50" : "bg-gray-50"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`text-xs ${theme.page.includes("950") ? "text-gray-500" : "text-gray-400"}`}>{label}</p>
                  <p className={`text-sm font-mono ${theme.page.includes("950") ? "text-gray-200" : "text-gray-800"}`}>{config[key]}</p>
                </div>
                {key !== "bank_name" && <button
                  onClick={() => {
                    navigator.clipboard.writeText(config[key]);
                    const el = document.getElementById(`bank-copy-${key}-${block.id}`);
                    if (el) { el.dataset.copied = "1"; setTimeout(() => { el.dataset.copied = ""; }, 1500); }
                  }}
                  id={`bank-copy-${key}-${block.id}`}
                  className={`shrink-0 p-1.5 rounded-md transition ${
                    theme.page.includes("950")
                      ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Copy className="w-4 h-4" />
                </button>}
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "billing_info": {
      const billingFields = [
        { key: "rfc", label: t("public.billing.rfc") },
        { key: "business_name", label: t("public.billing.businessName") },
        { key: "zip_code", label: t("public.billing.zipCode") },
        { key: "tax_regime", label: t("public.billing.taxRegime") },
        { key: "cfdi_use", label: t("public.billing.cfdiUse") },
        { key: "billing_email", label: t("public.billing.billingEmail") },
      ].filter((f) => config[f.key]?.trim());
      if (!billingFields.length) return null;
      return (
        <div className={`${theme.card} p-5 lg:p-6`}>
          <h3 className={theme.sectionTitle}>{lt("public.billing.title")}</h3>
          <button
            onClick={() => {
              const url = `${window.location.origin}/p/${slug}#block-${block.id}`;
              const text = `${profile.profile_name} - ${lt("public.billing.title")}\n${url}`;
              if (navigator.share) {
                navigator.share({ text }).catch(() => {});
              } else {
                navigator.clipboard.writeText(text);
              }
            }}
            className={`mb-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium transition ${
              theme.page.includes("950")
                ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <Share2 className="w-3.5 h-3.5" />
            {t("public.share")}
          </button>
          <div className="space-y-2">
            {billingFields.map(({ key, label }) => (
              <div
                key={key}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg ${
                  theme.page.includes("950") ? "bg-gray-800/50" : "bg-gray-50"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`text-xs ${theme.page.includes("950") ? "text-gray-500" : "text-gray-400"}`}>{label}</p>
                  <p className={`text-sm font-mono ${theme.page.includes("950") ? "text-gray-200" : "text-gray-800"}`}>{config[key]}</p>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(config[key]);
                    const el = document.getElementById(`billing-copy-${key}-${block.id}`);
                    if (el) { el.dataset.copied = "1"; setTimeout(() => { el.dataset.copied = ""; }, 1500); }
                  }}
                  id={`billing-copy-${key}-${block.id}`}
                  className={`shrink-0 p-1.5 rounded-md transition ${
                    theme.page.includes("950")
                      ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "website": {
      const url = config.url || "";
      if (!url.trim()) return null;
      let displayUrl = url;
      try { displayUrl = new URL(url).hostname; } catch {}
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logProfileEvent(slug, "link_click", { label: lt() || displayUrl, url })}
          className={`${theme.card} p-4 lg:p-5 flex items-center gap-4 group hover:shadow-md transition`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: themeColor + "15" }}
          >
            <Globe className="w-5 h-5" style={{ color: themeColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${theme.page.includes("950") ? "text-gray-100" : "text-gray-900"}`}>
              {lt("public.blockTitle.website")}
            </p>
            <p className={`text-xs truncate ${theme.page.includes("950") ? "text-gray-400" : "text-gray-500"}`}>
              {displayUrl}
            </p>
          </div>
          <ExternalLink className={`w-4 h-4 flex-shrink-0 ${theme.page.includes("950") ? "text-gray-500" : "text-gray-400"} group-hover:translate-x-0.5 transition-transform`} />
        </a>
      );
    }

    case "contact_form":
      return <ContactFormBlock block={block} theme={theme} slug={slug} />;

    case "media": {
      const mediaUrl = config.media_url;
      if (!mediaUrl) return null;
      return (
        <div className={`${theme.card} overflow-hidden`}>
          {lt() && <h3 className={`${theme.sectionTitle} px-5 pt-5 lg:px-6 lg:pt-6`}>{lt()}</h3>}
          {config.media_type === "video" ? (
            <video src={mediaUrl} controls className="w-full" />
          ) : (
            <img src={mediaUrl} alt={lt() || ""} className="w-full" />
          )}
        </div>
      );
    }

    case "gallery": {
      const galleryId = config.gallery_id;
      if (!galleryId) return null;
      const gallery = galleries.find((g) => g.id === galleryId);
      if (!gallery || !gallery.items?.length) return null;
      return (
        <GalleryBlock block={block} gallery={gallery} theme={theme} />
      );
    }

    case "google_review": {
      const placeId = config.place_id || "";
      if (!placeId.trim()) return null;
      return (
        <GoogleReviewBlock block={block} placeId={placeId} theme={theme} slug={slug} themeColor={themeColor} />
      );
    }

    case "payment":
      return (
        <PaymentBlock block={block} theme={theme} slug={slug} themeColor={themeColor} />
      );

    case "appointment":
      return (
        <AppointmentBlock block={block} theme={theme} slug={slug} themeColor={themeColor} />
      );

    default:
      return null;
  }
}

/* ============================================================
   CONTACT FORM BLOCK
   ============================================================ */

function ContactFormBlock({ block, theme, slug }) {
  const { language, t } = usePublicTranslation();
  const config = block.config || {};
  const lt = (fallbackKey) => config.title_i18n?.[language] || (fallbackKey ? t(fallbackKey) : "");
  const showEmail = config.show_email !== false;
  const showWhatsapp = config.show_whatsapp !== false;
  const showMessage = config.show_message !== false;
  const buttonText = config.button_text || t("contactForm.submit");
  const successMsg = config.success_message || t("contactForm.success");

  const visitorId = React.useMemo(() => {
    let vid = localStorage.getItem("visitor_id");
    if (!vid) {
      vid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("visitor_id", vid);
    }
    return vid;
  }, []);

  const savedLead = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(`lead_${slug}`) || "null");
    } catch { return null; }
  }, [slug]);

  const [name, setName] = useState(savedLead?.name || "");
  const [email, setEmail] = useState(savedLead?.email || "");
  const [whatsapp, setWhatsapp] = useState(savedLead?.whatsapp || "");
  const [message, setMessage] = useState(savedLead?.message || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!savedLead?.lead_sent);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  const refParam = new URLSearchParams(window.location.search).get("ref") || "";
  const isUpdate = !!savedLead?.lead_sent;
  const showForm = !sent || editing;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSending(true);
    setError("");
    try {
      await submitLead(slug, {
        name: name.trim(),
        email: email.trim() || undefined,
        whatsapp: whatsapp.trim() || undefined,
        message: message.trim() || undefined,
        source_ref: refParam || undefined,
        visitor_id: visitorId,
      });
      localStorage.setItem(`lead_${slug}`, JSON.stringify({ name, email, whatsapp, message, lead_sent: true }));
      setSent(true);
      setEditing(false);
      if (!isUpdate) {
        logProfileEvent(slug, "contact_click", { type: "contact_form", ref: refParam || undefined });
      }
    } catch {
      setError(t("contactForm.error"));
    }
    setSending(false);
  };

  const isDark = theme.page.includes("950");
  const textClass = isDark ? "text-gray-200" : "text-gray-700";
  const subTextClass = isDark ? "text-gray-400" : "text-gray-500";

  if (sent && !editing) {
    return (
      <div className={`${theme.card} p-5 lg:p-6`}>
        <div className="flex items-center gap-2 mb-3">
          <Check className="h-5 w-5 text-green-500" />
          <p className={`text-sm font-medium ${textClass}`}>{successMsg}</p>
        </div>
        <div className={`text-xs space-y-1 ${subTextClass}`}>
          <p><span className="font-medium">{t("contactForm.nameLabel")}:</span> {name}</p>
          {email && <p><span className="font-medium">{t("contactForm.emailLabel")}:</span> {email}</p>}
          {whatsapp && <p><span className="font-medium">{t("contactForm.whatsappLabel")}:</span> {whatsapp}</p>}
          {message && <p><span className="font-medium">{t("contactForm.messageLabel")}:</span> {message}</p>}
        </div>
        <button
          onClick={() => setEditing(true)}
          className={`mt-3 text-xs font-medium ${isDark ? "text-indigo-400 hover:text-indigo-300" : "text-indigo-600 hover:text-indigo-700"} transition`}
        >
          {t("contactForm.edit")}
        </button>
      </div>
    );
  }

  return (
    <div className={`${theme.card} p-5 lg:p-6`}>
      <h3 className={theme.sectionTitle}>
        <MessageSquare className="h-4 w-4 inline mr-2 -mt-0.5" />
        {lt("contactForm.title")}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3 mt-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("contactForm.namePlaceholder")}
          required
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none"
        />
        {showEmail && (
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("contactForm.emailPlaceholder")}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none"
          />
        )}
        {showWhatsapp && (
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder={t("contactForm.whatsappPlaceholder")}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none"
          />
        )}
        {showMessage && (
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("contactForm.messagePlaceholder")}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none resize-none"
          />
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={sending || !name.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition disabled:opacity-50"
          style={{ backgroundColor: block.config?.button_color || "#6366f1" }}
        >
          {sending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {sending ? t("contactForm.sending") : isUpdate ? t("contactForm.update") : buttonText}
        </button>
        {editing && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className={`w-full text-xs ${subTextClass} hover:underline`}
          >
            {t("contactForm.cancel")}
          </button>
        )}
      </form>
    </div>
  );
}

/* ============================================================
   PAYMENT BLOCK
   ============================================================ */

function PaymentBlock({ block, theme, slug, themeColor }) {
  const { language, t } = usePublicTranslation();
  const config = block.config || {};
  const lt = (fallbackKey) => config.title_i18n?.[language] || (fallbackKey ? t(fallbackKey) : "");
  const items = (config.payment_items || []).filter((i) => i.name && i.price);
  const allowCustom = config.allow_custom_amount !== false;
  const buttonText = config.button_text || t("payment.pay");
  const description = config.description_i18n?.[language] || config.description || t("payment.defaultDescription");
  const isDark = theme.page.includes("950");

  const [quantities, setQuantities] = useState(() => items.map(() => 0));
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const reset = (e) => { if (e.persisted) setLoading(false); };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const itemsTotal = items.reduce((sum, item, i) => sum + (quantities[i] || 0) * item.price, 0);
  const customNum = parseFloat(customAmount) || 0;
  const total = itemsTotal + customNum;

  const updateQty = (idx, delta) => {
    setQuantities((prev) => prev.map((q, i) => i === idx ? Math.max(0, q + delta) : q));
  };

  const handlePay = async () => {
    if (total <= 0) {
      setError(t("payment.invalidAmount"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await createProfilePayment(slug, {
        block_id: block.id,
        amount: total,
      });
      if (res.data.init_point) {
        window.location.href = res.data.init_point;
      }
    } catch (e) {
      setError(t("payment.error"));
      setLoading(false);
    }
  };

  return (
    <div className={`${theme.card} p-4 lg:p-5`}>
      {lt() && <h3 className={theme.sectionTitle}>{lt()}</h3>}
      {description && <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"} mb-3`}>{description}</p>}
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className={`flex items-center justify-between gap-2 p-2.5 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}>{item.name}</p>
              <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>${parseFloat(item.price).toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => updateQty(idx, -1)}
                disabled={!quantities[idx]}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition ${
                  quantities[idx] ? (isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300") : (isDark ? "bg-gray-800 text-gray-600" : "bg-gray-100 text-gray-300")
                }`}
              >−</button>
              <span className={`w-6 text-center text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>{quantities[idx] || 0}</span>
              <button
                onClick={() => updateQty(idx, 1)}
                className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition"
                style={{ backgroundColor: themeColor, color: "white" }}
              >+</button>
            </div>
          </div>
        ))}

        {allowCustom && (
          <div className={`flex items-center justify-between gap-2 p-2.5 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
            <p className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}>{t("payment.customAmount")}</p>
            <div className="relative w-28 flex-shrink-0">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
              <input
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className={`w-full pl-6 pr-2 py-1.5 text-sm rounded-lg border ${isDark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-800"}`}
              />
            </div>
          </div>
        )}

        {(items.length > 0 || customNum > 0) && total > 0 && (
          <div className={`flex items-center justify-between pt-2 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <span className={`text-sm font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total</span>
            <span className="text-lg font-bold" style={{ color: themeColor }}>${total.toFixed(2)} MXN</span>
          </div>
        )}

        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          onClick={handlePay}
          disabled={loading || total <= 0}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition disabled:opacity-50"
          style={{ backgroundColor: themeColor }}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <DollarSign className="w-4 h-4" />
          )}
          {loading ? t("payment.processing") : `${buttonText}${total > 0 ? ` $${total.toFixed(2)}` : ""}`}
        </button>
        <button
          onClick={async () => {
            const url = `${window.location.origin}/p/${slug}#block-${block.id}`;
            const text = `${lt() || t("payment.pay")} - Mercado Pago\n${url}`;
            if (navigator.share) {
              try { await navigator.share({ text }); } catch {}
            } else {
              await navigator.clipboard.writeText(text);
            }
            logProfileEvent(slug, "payment_share");
          }}
          className={`w-full flex items-center justify-center gap-2 py-2 text-xs font-medium rounded-lg transition ${
            theme.page.includes("950") ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <Share2 className="w-3.5 h-3.5" />
          {t("payment.share")}
        </button>
      </div>
    </div>
  );
}


/* ============================================================
   APPOINTMENT BLOCK
   ============================================================ */

function AppointmentBlock({ block, theme, slug, themeColor }) {
  const { language, t } = usePublicTranslation();
  const config = block.config || {};
  const lt = (fallbackKey) => config.title_i18n?.[language] || (fallbackKey ? t(fallbackKey) : "");
  const isDark = theme.page.includes("950");

  const [step, setStep] = useState("services");
  const [selectedService, setSelectedService] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [form, setForm] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(`lead_${slug}`) || "{}"); return { name: s.name || "", email: s.email || "", whatsapp: s.whatsapp || "", notes: "" }; } catch { return { name: "", email: "", whatsapp: "", notes: "" }; }
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);
  const [showMyAppts, setShowMyAppts] = useState(false);
  const [myAppts, setMyAppts] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [bookError, setBookError] = useState("");
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bitsness_dismissed_appts") || "[]"); } catch { return []; }
  });
  const dismissAppt = (id) => {
    const updated = [...dismissedIds, id];
    setDismissedIds(updated);
    localStorage.setItem("bitsness_dismissed_appts", JSON.stringify(updated));
  };
  const visibleAppts = myAppts.filter((a) => !dismissedIds.includes(a.id));
  const timeSlotsRef = React.useRef(null);
  const blockTopRef = React.useRef(null);

  const services = config.service_types || [];

  useEffect(() => {
    if (selectedService) {
      getAvailableSlots(slug, { service_id: selectedService.id }).then((r) => {
        setSlotsData(r.data);
      }).catch(() => {});
    }
  }, [slug, selectedService]);

  const loadMyAppointments = async () => {
    setLoadingAppts(true);
    try {
      const r = await getVisitorAppointments(slug);
      setMyAppts(r.data.appointments || []);
    } catch {}
    setLoadingAppts(false);
  };

  const handleCancel = async () => {
    if (!cancellingId) return;
    setCancelLoading(true);
    try {
      await cancelVisitorAppointment(slug, cancellingId);
      setCancellingId(null);
      loadMyAppointments();
    } catch {}
    setCancelLoading(false);
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selectedService || !selectedDate || !selectedTime || !form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setBookError("");
    try {
      const r = await bookAppointment(slug, {
        service_id: selectedService.id,
        service_name: selectedService.name,
        date: selectedDate,
        start_time: selectedTime,
        visitor_name: form.name.trim(),
        visitor_email: form.email.trim(),
        visitor_whatsapp: form.whatsapp.trim() || undefined,
        visitor_notes: form.notes.trim() || undefined,
      });
      localStorage.setItem(`lead_${slug}`, JSON.stringify({ name: form.name.trim(), email: form.email.trim(), whatsapp: form.whatsapp.trim() }));
      setBookedAppt(r.data.appointment);
      setStep("success");
    } catch (err) {
      setBookError(err.response?.data?.error || "Error");
    }
    setSubmitting(false);
  };

  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
  const availableDates = slotsData ? Object.keys(slotsData.available_slots || {}) : [];
  const slotsForDate = selectedDate && slotsData ? (slotsData.available_slots?.[selectedDate] || []) : [];

  const statusColors = { pending: "bg-yellow-100 text-yellow-700", confirmed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700", completed: "bg-blue-100 text-blue-700" };

  if (showMyAppts) {
    return (
      <div className={`${theme.card} p-5 lg:p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={theme.sectionTitle}>
            <Calendar className="h-4 w-4 inline mr-2 -mt-0.5" />
            {t("appointment.myAppointments")}
          </h3>
          <button onClick={() => setShowMyAppts(false)} className="text-xs" style={{ color: themeColor }}>{t("appointment.bookNow")}</button>
        </div>
        {loadingAppts ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : visibleAppts.length === 0 ? (
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t("appointment.noAppointments")}</p>
        ) : (
          <div className="space-y-2">
            {visibleAppts.map((a) => (
              <div key={a.id} className={`border rounded-lg p-3 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{a.service_name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[a.status] || ""}`}>
                    {t(`appointment.${a.status}`) || a.status}
                  </span>
                </div>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {new Date(a.date + "T00:00").toLocaleDateString(language === "es" ? "es-MX" : "en-US", { weekday: "short", month: "short", day: "numeric" })} &middot; {a.start_time} - {a.end_time}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  {(a.status === "pending" || a.status === "confirmed") && (
                    <>
                      <button onClick={() => setCancellingId(a.id)} className="text-[11px] text-red-500 hover:text-red-600">{t("appointment.cancel")}</button>
                      <a href={getAppointmentIcalUrl(slug, a.id)} download className="text-[11px] hover:underline" style={{ color: themeColor }}>
                        <Download className="w-3 h-3 inline mr-0.5" />{t("appointment.downloadCal")}
                      </a>
                    </>
                  )}
                  <button onClick={() => dismissAppt(a.id)} className="text-[11px] ml-auto text-orange-500 hover:text-orange-600">
                    <XIcon className="w-3 h-3 inline mr-0.5" />{t("appointment.dismiss")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cancel confirmation modal */}
        {cancellingId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !cancelLoading && setCancellingId(null)}>
            <div className="bg-white rounded-2xl w-full max-w-xs mx-4 p-5 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">{t("appointment.cancelConfirm")}</h3>
              <p className="text-xs text-gray-500 mb-4">{t("appointment.cancelDesc")}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCancellingId(null)}
                  disabled={cancelLoading}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  {t("appointment.keepIt")}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelLoading}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-60"
                >
                  {cancelLoading ? "..." : t("appointment.cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (step === "success" && bookedAppt) {
    const isAuto = bookedAppt.status === "confirmed";
    return (
      <div className={`${theme.card} p-5 lg:p-6 text-center`}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: themeColor + "20" }}>
          <Check className="w-6 h-6" style={{ color: themeColor }} />
        </div>
        <h3 className={`text-lg font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>{t("appointment.success")}</h3>
        <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {isAuto ? t("appointment.successAuto") : t("appointment.successManual")}
        </p>
        <div className={`rounded-lg p-3 mb-4 text-left text-sm ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
          <p className={isDark ? "text-white" : "text-gray-900"}><strong>{bookedAppt.service_name}</strong></p>
          <p className={isDark ? "text-gray-400" : "text-gray-500"}>
            {new Date(bookedAppt.date + "T00:00").toLocaleDateString(language === "es" ? "es-MX" : "en-US", { weekday: "long", month: "long", day: "numeric" })}
            {" "}&middot; {bookedAppt.start_time} - {bookedAppt.end_time}
          </p>
          {bookedAppt.service_price > 0 && <p style={{ color: themeColor }} className="font-medium">${bookedAppt.service_price.toFixed(2)}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <a href={getAppointmentIcalUrl(slug, bookedAppt.id)} download
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: themeColor }}>
            <Download className="w-4 h-4" /> {t("appointment.downloadCal")}
          </a>
          <button onClick={() => { setStep("services"); setSelectedService(null); setSelectedDate(null); setSelectedTime(null); setBookedAppt(null); }}
            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {t("appointment.bookNow")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={blockTopRef} className={`${theme.card} p-5 lg:p-6`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={theme.sectionTitle}>
          <Calendar className="h-4 w-4 inline mr-2 -mt-0.5" />
          {lt("editor.appointment.title")}
        </h3>
        <button onClick={() => { setShowMyAppts(true); loadMyAppointments(); }}
          className="text-[11px] font-medium" style={{ color: themeColor }}>
          {t("appointment.myAppointments")}
        </button>
      </div>

      {/* Step 1: Select Service */}
      {step === "services" && (
        <div className="space-y-2">
          <p className={`text-xs font-medium mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t("appointment.selectService")}</p>
          {services.map((svc) => (
            <button key={svc.id} onClick={() => { setSelectedService(svc); setStep("calendar"); }}
              className={`w-full text-left border rounded-lg p-3 transition ${isDark ? "border-gray-700 hover:border-gray-500" : "border-gray-200 hover:border-gray-400"}`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{svc.name}</span>
                <span className="text-sm font-semibold" style={{ color: themeColor }}>
                  {svc.price > 0 ? `$${svc.price.toFixed(2)}` : t("appointment.free")}
                </span>
              </div>
              <div className={`flex items-center gap-2 text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                <Clock className="w-3 h-3" /> {svc.duration} {t("appointment.min")}
              </div>
              {svc.description && <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{svc.description}</p>}
            </button>
          ))}
          {services.length === 0 && (
            <button onClick={() => { setSelectedService({ id: "default", name: lt("editor.appointment.title"), duration: config.slot_duration || 30, price: 0 }); setStep("calendar"); }}
              className="w-full text-left border rounded-lg p-3" style={{ borderColor: themeColor + "40" }}>
              <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{lt("editor.appointment.title")}</span>
              <div className={`flex items-center gap-2 text-xs mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                <Clock className="w-3 h-3" /> {config.slot_duration || 30} {t("appointment.min")}
              </div>
            </button>
          )}
        </div>
      )}

      {/* Step 2: Calendar + Time */}
      {step === "calendar" && (
        <div>
          <button onClick={() => { setStep("services"); setSelectedDate(null); setSelectedTime(null); }}
            className={`text-xs mb-3 flex items-center gap-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <ChevronLeft className="w-3 h-3" /> {selectedService?.name}
          </button>

          {/* Calendar grid */}
          <div className={`rounded-lg border p-3 mb-3 ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} className="p-1">
                <ChevronLeft className="w-4 h-4" style={{ color: themeColor }} />
              </button>
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                {calMonth.toLocaleDateString(language === "es" ? "es-MX" : "en-US", { month: "long", year: "numeric" })}
              </span>
              <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} className="p-1">
                <ChevronRight className="w-4 h-4" style={{ color: themeColor }} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium mb-1">
              {(language === "es" ? ["Lu","Ma","Mi","Ju","Vi","Sá","Do"] : ["Mo","Tu","We","Th","Fr","Sa","Su"]).map((d) => (
                <div key={d} className={isDark ? "text-gray-500" : "text-gray-400"}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: (firstDayOfWeek + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const hasSlots = availableDates.includes(dateStr);
                const isSelected = selectedDate === dateStr;
                const today = new Date(); today.setHours(0,0,0,0);
                const isPast = new Date(dateStr) < today;
                return (
                  <button
                    key={day}
                    disabled={!hasSlots || isPast}
                    onClick={() => { setSelectedDate(dateStr); setSelectedTime(null); setTimeout(() => timeSlotsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); }}
                    className={`w-full aspect-square rounded-lg text-xs font-medium transition
                      ${isSelected ? "text-white" : hasSlots && !isPast ? (isDark ? "text-white hover:bg-gray-700" : "text-gray-900 hover:bg-gray-100") : (isDark ? "text-gray-600" : "text-gray-300")}
                    `}
                    style={isSelected ? { backgroundColor: themeColor } : hasSlots && !isPast ? {} : {}}
                  >
                    {day}
                    {hasSlots && !isPast && !isSelected && <div className="w-1 h-1 rounded-full mx-auto mt-0.5" style={{ backgroundColor: themeColor }} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          {selectedDate && (
            <div ref={timeSlotsRef}>
              <p className={`text-xs font-medium mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t("appointment.selectTime")}</p>
              {slotsForDate.length === 0 ? (
                <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>{t("appointment.noSlots")}</p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5 mb-3">
                  {slotsForDate.map((slot) => (
                    <button
                      key={slot.start}
                      onClick={() => setSelectedTime(slot.start)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition
                        ${selectedTime === slot.start
                          ? "text-white border-transparent"
                          : isDark ? "border-gray-700 text-gray-300 hover:border-gray-500" : "border-gray-200 text-gray-700 hover:border-gray-400"
                        }
                      `}
                      style={selectedTime === slot.start ? { backgroundColor: themeColor } : {}}
                    >
                      {slot.start}
                    </button>
                  ))}
                </div>
              )}
              {selectedTime && (
                <button
                  onClick={() => { setStep("form"); setTimeout(() => blockTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition"
                  style={{ backgroundColor: themeColor }}
                >
                  {t("appointment.confirm")} &rarr;
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Contact Form */}
      {step === "form" && (
        <div>
          <button onClick={() => setStep("calendar")}
            className={`text-xs mb-3 flex items-center gap-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <ChevronLeft className="w-3 h-3" />
            {new Date(selectedDate + "T00:00").toLocaleDateString(language === "es" ? "es-MX" : "en-US", { month: "short", day: "numeric" })} &middot; {selectedTime}
          </button>
          <p className={`text-xs font-medium mb-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{t("appointment.yourInfo")}</p>
          <form onSubmit={handleBook} className="space-y-2.5">
            <input
              type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("appointment.name")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
            />
            <input
              type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("appointment.email")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
            />
            <input
              type="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              placeholder={t("appointment.whatsapp")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
            />
            <textarea
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t("appointment.notes")} rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 resize-none"
            />

            {/* Summary */}
            <div className={`rounded-lg p-3 text-xs ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
              <p className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{selectedService?.name}</p>
              <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                {new Date(selectedDate + "T00:00").toLocaleDateString(language === "es" ? "es-MX" : "en-US", { weekday: "long", month: "long", day: "numeric" })}
                {" "}&middot; {selectedTime}
              </p>
              {selectedService?.price > 0 && <p className="font-semibold mt-0.5" style={{ color: themeColor }}>${selectedService.price.toFixed(2)}</p>}
              {slotsData?.config?.location_type === "address" && slotsData?.config?.location_address && (
                <p className={`mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  <MapPin className="w-3 h-3 inline mr-0.5" /> {slotsData.config.location_address}
                </p>
              )}
              {slotsData?.config?.location_type === "video" && (
                <p className={`mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>{t("appointment.videoCall")}</p>
              )}
              {slotsData?.config?.location_type === "phone" && (
                <p className={`mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>{t("appointment.phoneCall")}</p>
              )}
            </div>

            {bookError && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-2">{bookError}</p>}
            <button
              type="submit" disabled={submitting}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-60"
              style={{ backgroundColor: themeColor }}
            >
              {submitting ? t("appointment.confirming") : t("appointment.confirm")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}


/* ============================================================
   GOOGLE REVIEW BLOCK
   ============================================================ */

function GoogleReviewBlock({ block, placeId, theme, slug, themeColor }) {
  const { language, t } = usePublicTranslation();
  const config = block.config || {};
  const lt = (fallbackKey) => config.title_i18n?.[language] || (fallbackKey ? t(fallbackKey) : "");
  const isDark = theme.page.includes("950");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedStar, setSelectedStar] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reviewUrl = `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;

  // Check if already reviewed today
  const reviewKey = `review_${slug}`;
  const getStoredReview = () => {
    try {
      const stored = localStorage.getItem(reviewKey);
      if (!stored) return null;
      const { date, rating } = JSON.parse(stored);
      const today = new Date().toDateString();
      if (date === today) return rating;
      localStorage.removeItem(reviewKey);
    } catch {}
    return null;
  };

  const storedRating = getStoredReview();
  const [phase, setPhase] = useState(storedRating ? "already" : "loading");

  const visitorId = React.useMemo(() => {
    let vid = localStorage.getItem("visitor_id");
    if (!vid) {
      vid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("visitor_id", vid);
    }
    return vid;
  }, []);

  React.useEffect(() => {
    if (storedRating) return; // already know from localStorage
    checkReview(slug, visitorId)
      .then((res) => {
        setPhase(res.data.reviewed ? "already" : "stars");
      })
      .catch(() => setPhase("stars"));
  }, [slug, visitorId, storedRating]);

  const doSubmit = async (rating, feedbackText) => {
    try {
      await submitReview(slug, { rating, feedback: feedbackText || "", visitor_id: visitorId });
      localStorage.setItem(reviewKey, JSON.stringify({ date: new Date().toDateString(), rating }));
    } catch (err) {
      if (err.response?.status === 429) {
        setPhase("already");
        return false;
      }
    }
    return true;
  };

  const handleStarClick = async (rating) => {
    setSelectedStar(rating);
    if (rating >= 4) {
      // Good rating → store + redirect to Google
      const ok = await doSubmit(rating, "");
      if (ok) {
        setPhase("done");
        window.open(reviewUrl, "_blank", "noopener,noreferrer");
      }
    } else {
      // Low rating → ask for feedback
      setPhase("feedback");
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    const ok = await doSubmit(selectedStar, feedback);
    setSubmitting(false);
    if (ok) setPhase("done");
  };

  const starLabels = [
    t("review.star1"),
    t("review.star2"),
    t("review.star3"),
    t("review.star4"),
    t("review.star5"),
  ];

  const activeStar = hoveredStar || selectedStar;

  return (
    <div className={`${theme.card} p-5 lg:p-6`}>
      <h3 className={`text-sm font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
        {lt("review.title")}
      </h3>

      {phase === "loading" && (
        <div className="flex justify-center py-6">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-8 h-8 ${isDark ? "text-gray-700" : "text-gray-200"}`} />
            ))}
          </div>
        </div>
      )}

      {phase === "already" && (
        <div className="text-center py-4">
          <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {t("review.alreadyReviewed")}
          </p>
        </div>
      )}

      {phase === "done" && (
        <div className="text-center py-4">
          <div className="flex justify-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`w-6 h-6 ${i <= selectedStar ? "text-yellow-400 fill-yellow-400" : isDark ? "text-gray-600" : "text-gray-300"}`}
              />
            ))}
          </div>
          <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {t("review.thanks")}
          </p>
          {selectedStar >= 4 && (
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-3 text-xs font-medium underline"
              style={{ color: themeColor }}
            >
              {t("review.openGoogle")}
            </a>
          )}
        </div>
      )}

      {phase === "feedback" && (
        <div className="space-y-3">
          <div className="flex justify-center gap-1 mb-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`w-5 h-5 ${i <= selectedStar ? "text-yellow-400 fill-yellow-400" : isDark ? "text-gray-600" : "text-gray-300"}`}
              />
            ))}
          </div>
          <p className={`text-xs text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {t("review.feedbackPrompt")}
          </p>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            maxLength={2000}
            className={`w-full px-3 py-2 text-sm rounded-lg border resize-none focus:outline-none focus:ring-2 ${
              isDark
                ? "bg-gray-800 border-gray-700 text-white focus:ring-gray-600"
                : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
            }`}
            placeholder={t("review.feedbackPlaceholder")}
            autoFocus
          />
          <button
            onClick={handleFeedbackSubmit}
            disabled={submitting || !feedback.trim()}
            className="w-full py-2.5 text-sm font-medium rounded-lg text-white disabled:opacity-50 transition"
            style={{ backgroundColor: themeColor }}
          >
            {submitting ? t("review.sending") : t("review.sendFeedback")}
          </button>
        </div>
      )}

      {phase === "stars" && (
        <>
          <p className={`text-xs mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {t("review.tapToRate")}
          </p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                onMouseEnter={() => setHoveredStar(i)}
                onMouseLeave={() => setHoveredStar(0)}
                onClick={() => handleStarClick(i)}
                className="p-1 transition-transform hover:scale-125 focus:outline-none"
                aria-label={`${i} star`}
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    i <= activeStar
                      ? "text-yellow-400 fill-yellow-400"
                      : isDark
                        ? "text-gray-600"
                        : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          {activeStar > 0 && (
            <p className={`text-center text-xs mt-2 font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {starLabels[activeStar - 1]}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ============================================================
   GALLERY BLOCK (with lightbox)
   ============================================================ */

function GalleryBlock({ block, gallery, theme }) {
  const { language } = usePublicTranslation();
  const config = block.config || {};
  const lt = () => config.title_i18n?.[language] || "";
  const [lightboxIndex, setLightboxIndex] = useState(null);

  return (
    <div className={`${theme.card} p-5 lg:p-6`}>
      {lt() && <h3 className={theme.sectionTitle}>{lt()}</h3>}
      <div className={theme.galleryGrid}>
        {gallery.items.map((item, idx) => (
          <GalleryItem
            key={item.id}
            item={item}
            theme={theme}
            onClick={() => setLightboxIndex(idx)}
          />
        ))}
      </div>
      {lightboxIndex !== null && (
        <GalleryLightbox
          items={gallery.items}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

/* ============================================================
   GALLERY ITEM
   ============================================================ */

function GalleryItem({ item, theme, onClick }) {
  const isVideo = item.media_type === "video";
  const is360 = item.media_type === "image_360";
  const url = item.file_url || item.url;

  return (
    <div
      className={`relative aspect-square overflow-hidden bg-gray-100 group cursor-pointer ${theme.galleryItem}`}
      onClick={onClick}
    >
      {isVideo ? (
        <>
          {item.thumbnail_url ? (
            <img src={item.thumbnail_url} alt={item.title || "Video"} className="w-full h-full object-cover" />
          ) : (
            <video src={url} muted preload="metadata" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[14px] border-l-gray-800 ml-1" />
            </div>
          </div>
        </>
      ) : (
        <>
          <img src={url} alt={item.title || "Image"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {is360 && (
            <span className="absolute top-2 left-2 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded-md">360</span>
          )}
        </>
      )}
      {/* Caption overlay */}
      {(item.title || item.link_url) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
          {item.title && <p className="text-white text-xs truncate">{item.title}</p>}
          {item.link_url && <ExternalLink className="absolute top-2 right-2 w-3.5 h-3.5 text-white/70" />}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   GALLERY LIGHTBOX (carousel)
   ============================================================ */

function GalleryLightbox({ items, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [animDir, setAnimDir] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const item = items[index];
  const url = item?.file_url || item?.url;
  const isVideo = item?.media_type === "video";
  const total = items.length;

  const goTo = useCallback((newIdx, dir) => {
    if (newIdx < 0 || newIdx >= total) return;
    setAnimDir(dir);
    setTimeout(() => {
      setIndex(newIdx);
      setAnimDir(null);
    }, 150);
  }, [total]);

  const goPrev = useCallback(() => goTo(index - 1, "left"), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1, "right"), [goTo, index]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, goPrev, goNext]);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goNext() : goPrev();
    }
    setTouchStart(null);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
      >
        <XIcon className="w-6 h-6" />
      </button>

      {/* Counter */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white/80 text-sm font-medium">
        {index + 1} / {total}
      </div>

      {/* Previous */}
      {index > 0 && (
        <button
          onClick={goPrev}
          className="absolute left-2 sm:left-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition"
        >
          <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}

      {/* Next */}
      {index < total - 1 && (
        <button
          onClick={goNext}
          className="absolute right-2 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition"
        >
          <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}

      {/* Media */}
      <div className={`relative z-[1] max-w-[90vw] max-h-[85vh] flex items-center justify-center transition-all duration-150 ${
        animDir === "left" ? "opacity-0 translate-x-8" : animDir === "right" ? "opacity-0 -translate-x-8" : "opacity-100 translate-x-0"
      }`}>
        {isVideo ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={url}
            alt={item?.title || ""}
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
            draggable={false}
          />
        )}
      </div>

      {/* Caption & Link */}
      {(item?.title || item?.link_url) && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 px-4 py-2.5 rounded-xl bg-black/50 backdrop-blur-md max-w-[85vw] text-center space-y-1">
          {item.title && (
            <p className="text-white text-sm font-medium">{item.title}</p>
          )}
          {item.link_url && (
            <a
              href={item.link_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-xs transition"
            >
              <ExternalLink className="w-3 h-3" />
              <span className="truncate max-w-[60vw]">{item.link_text || item.link_url.replace(/^https?:\/\//, '')}</span>
            </a>
          )}
        </div>
      )}

      {/* Dot indicators */}
      {total <= 20 && (
        <div className={`absolute ${item?.title || item?.link_url ? "bottom-20" : "bottom-16"} left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5`}>
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i, i > index ? "right" : "left")}
              className={`rounded-full transition-all duration-200 ${
                i === index
                  ? "w-2.5 h-2.5 bg-white"
                  : "w-1.5 h-1.5 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
